import Pusher from 'pusher-js';
import Connection from './Connection';
import PrivateChannel from './Channels/PrivateChannel';
import PresenceChannel from './Channels/PresenceChannel';
import ChannelsManager from './Channels/ChannelsManager';

const CONNECTION_STATUSES = {
    INITIALIZED: 'initialized',
    CONNECTING: 'connecting',
    CONNECTED: 'connected',
    UNAVAILABLE: 'unavailable',
    FAILED: 'failed',
    DISCONNECTED: 'disconnected'
};

const pusherAuthorizers = Pusher.Runtime.getAuthorizers();

pusherAuthorizers.batchAuthorization = function(context, socketId, callback) {

    const { authOptions, channel } = this;
    const authCredentials = authOptions.preAuth[channel.name];

    if (authCredentials) {
        return callback(false, authCredentials);
    }

    callback(true, `You need to run authorize for channel: ${channel.name}`);
};

Pusher.Runtime.getAuthorizers = () => pusherAuthorizers;

export default class PusherConnection extends Connection {

    /**
     * @constructs [PusherConnection]
     * @property {object} pusher - Pusher API instance
     */
    constructor(pusher) {
        super();
        this.pusher = pusher;

        this.initChannelsCollection = this.initChannelsCollection.bind(this);
        this.subscribe = this.subscribe.bind(this);
        this.unsubscribe = this.unsubscribe.bind(this);
        this.stateChange = this.stateChange.bind(this);
        this.disconnect = this.disconnect.bind(this);
        this.state = this.state.bind(this);
        this.stateChange = this.stateChange.bind(this);
        this.getChannel = this.getChannel.bind(this);

        this.initChannelsCollection();
    }

    initChannelsCollection() {
        for (const channelClass of [PrivateChannel, PresenceChannel]) {
            this.channels[channelClass.name] = {};
        }
    }

    /**
     * @returns {object} Object contains all connection statuses
     */
    static get connectionStatuses() {
        return CONNECTION_STATUSES;
    }

    /**
     * @property {string} appKey - RTE provider app key
     * @property {string} cluster - RTE provider cluster
     * @property {function} logger - Enable and get connection and channel logs
     * @property {boolean} debugMode - Enable debug mode
     * @property {string[]} privateChannelNames - list of private channel names which will be preauthorized
     * @property {string[]} presenceChannelNames - list of presence channel names which will be preauthorized
     * @returns {Promise<PusherConnection>} Promise which return <PusherConnection> in resolve or (ParamsValidationError, WSConnectionError) error in reject
     */
    static connect(appKey, cluster, logger, debugMode, privateChannelNames, presenceChannelNames) {

        return new Promise((resolve, reject) => {
            const pusher = new Pusher(appKey, {
                cluster,
                forceTLS: true,
                auth: {
                    preAuth: {}
                },
                authTransport: 'batchAuthorization'
            });

            if (debugMode) {
                Pusher.logToConsole = true;
            }

            pusher.connection.bind('connected', () => {
                const connection = new PusherConnection(pusher);

                if (!privateChannelNames && !presenceChannelNames) {
                    resolve(connection);
                    return;
                }

                const prefixedChannelNames = [
                    ...PrivateChannel.addPrefix(privateChannelNames),
                    ...PresenceChannel.addPrefix(presenceChannelNames)
                ];

                ChannelsManager.postAuthorization(
                    pusher,
                    prefixedChannelNames
                ).then(() => {
                    resolve(connection);
                }).catch((error) => {
                    reject(error);
                });
            });

            pusher.connection.bind('error', (err) => {
                if (logger) {
                    logger(`Pusher connection error: ${JSON.stringify(err)}`);
                }
                reject(err);
            });
        });
    }

    /**
     * @property {function} callback - Callback function which will be triggered on connection state change with state object
     */
    stateChange(callback) {
        this.pusher.connection.bind('state_change', (states) => {
            // states = {previous: 'oldState', current: 'newState'}
            callback(states);
        });
    }

    /**
     * @return {Void}
     */
    disconnect() {
        this.pusher.disconnect();
    }

    /**
     * @returns {string} string represent connection state: 'initialized', 'connecting', 'connected', 'unavailable', 'failed', 'disconnected'
     */
    state() {
        return this.pusher.connection.state;
    }

    /**
     * @property {string} channelName - Channel name
     * @property {function} channelClass - Channel type class
     * @return {Promise<PrivateChannel | PresenceChannel>} Promise which return <PrivateChannel | PresenceChannel> in resolve or ChannelSubscriptionError in reject
     */
    subscribe(channelName, channelClass) {
        const channelType = channelClass.name;
        const channel = this.channels[channelType][channelName];
        if (channel) {
            return Promise.resolve(channel);
        }

        return ChannelsManager.subscribe(
            this.pusher,
            channelName,
            channelClass
        ).then((channel) => {
            this.channels[channelType][channelName] = channel;
            return channel;
        });
    }

    /**
     * @property {string} channelName - Channel name
     * @property {function} channelClass - Channel type class
     */
    unsubscribe(channelName, channelClass) {
        const channelType = channelClass.name;
        if (this.channels[channelType][channelName]) {
            delete this.channels[channelType][channelName];
        }

        ChannelsManager.unsubscribe(this.pusher, channelName, channelClass);
    }

    /**
     * @returns {PrivateChannel | PresenceChannel} an instance of [PrivateChannel | PresenceChannel] class with the given name and type
     */
    getChannel(channelName, channelClass) {
        return this.channels[channelClass.name][channelName];
    }
}
